/**
 * apps/command/src/screens/StaffScreen.tsx — «Xodimlar».
 *
 * ============================================================================
 * CONSENT IS A STEP ON THE SCREEN, NOT A CHECKBOX BESIDE THE UPLOAD.
 * ============================================================================
 *
 * The database refuses an enrolment without recorded consent
 * (`staff_credentials_enrollment_needs_consent`, D-MBT-16) and that refusal is
 * the guarantee. What this screen has to do is make the ORDER obvious, so the
 * refusal never happens: a person's card shows consent first, and the upload
 * control does not exist until it is signed.
 *
 * A checkbox labelled «consent given» beside the file picker would satisfy the
 * constraint and defeat its purpose — the point of §8.3 is a paper form with a
 * reference somebody can produce, not a tick an operator makes on behalf of the
 * person whose face it is.
 *
 * ============================================================================
 * GROUPED BY MAHALLA, BECAUSE THAT IS HOW THE PEOPLE ARE GROUPED.
 * ============================================================================
 *
 * A flat list of 225 cards sorted by name is a list nobody can use: staff
 * belong to a building, «who works in Farovon» is the question an HR officer
 * arrives with, and §2.3's yettiligi is defined as one set of roles PER
 * MAHALLA. The «add» control lives inside each mahalla's group for the same
 * reason — an employee is added TO somewhere, and a form with a mahalla
 * dropdown invites picking the wrong one.
 *
 * ============================================================================
 * IT EDITS `yettiligi_members`, WHICH IS THE CITIZEN-FACING CARD.
 * ============================================================================
 *
 * D-MBT-14: attendance attaches to the yettiligi record and there is no second
 * staff table. So a name changed here is the name a citizen reads on the kiosk
 * in their mahalla, and the screen says so rather than letting somebody
 * discover it. That is also why editing needs `yettiligi.write` — granted to
 * `hr` on 2026-08-19 — while the biometric half needs `mbt.staff.manage`: two
 * different powers over one row.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components, paths } from '@navoiy/api-types';
import { api, ApiError, apiBaseUrl, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { stamp } from '../lib/format.js';
import { Button, Field, Notice, Panel, PanelHeader, Skeleton, TextInput } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';

/* The list item type, taken from the OPERATION's response rather than a named
   schema — the endpoint declares its items inline, and pointing at the response
   means this cannot drift from what the server actually sends. */
type StaffRow =
  components['schemas'] extends never
    ? never
    : NonNullable<
        paths['/mbt/staff']['get']['responses']['200']['content']['application/json']['items']
      >[number];

/** JPEG and PNG only — what the server can decode without node-canvas. */
/*
  ==========================================================================
  `image/*`, NOT `image/jpeg,image/png` — AND THE REASON IS THE FILE DIALOG.
  ==========================================================================

  `accept` does not validate anything; it decides what the OS picker will let
  somebody CLICK. A phone photo whose type the operating system reports as
  anything else — HEIC, an empty string because the extension is unfamiliar, a
  file copied off a device — is greyed out and cannot be selected at all. From
  the operator's side that is «the image is not being uploaded», with no error,
  because no request was ever made.

  So the picker is opened wide and the bytes are checked HERE instead, where a
  refusal can say what is wrong. The server checks again regardless; this is
  about giving somebody a sentence instead of a dead file dialog.
*/
const ACCEPT = 'image/*';

/**
 * The real type, from the first bytes — not from `file.type`.
 *
 * `file.type` is whatever the operating system guessed from the extension, and
 * it is EMPTY often enough to matter. Sending an empty `Content-Type` makes
 * Fastify refuse the body with a 415 before any route sees it, which reaches
 * the operator as a generic failure on a photograph that is perfectly good.
 *
 * Two magic numbers settle it: `FF D8 FF` is JPEG, `89 50 4E 47` is PNG.
 * Anything else — HEIC from an iPhone being the one that will actually happen —
 * is refused here with a sentence naming the format, rather than being sent to
 * a decoder that can only answer «not an image».
 */
async function sniffImageType(file: File): Promise<'image/jpeg' | 'image/png' | null> {
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'image/jpeg';
  if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) {
    return 'image/png';
  }
  return null;
}

/**
 * §2.3's yettiligi roles, in the order the citizen card lists them.
 *
 * KEYS, NEVER DISPLAY STRINGS — the contract says so, and `roleLabel` is the
 * only place one becomes words. A hardcoded «Mahalla raisi» in a dropdown would
 * be the first uz-Latn string in this panel that `t()` did not own.
 */
const YETTILIGI_ROLES = [
  'raisi',
  'hokim_yordamchisi',
  'yoshlar_yetakchisi',
  'xotin_qizlar_faoli',
  'profilaktika_inspektori',
  'ijtimoiy_xodim',
  'soliq_inspektori',
  'banker',
] as const;
type YettiligiRole = (typeof YETTILIGI_ROLES)[number];

function roleLabel(role: string): string {
  return hasKey(`staff.role.${role}`) ? t(`staff.role.${role}`) : role;
}

export function StaffScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const mayManage = allows('mbt.staff.manage');

  const [items, setItems] = useState<StaffRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const mayEdit = allows('yettiligi.write');
  /** The member whose name/role is being edited, and the draft. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  /** The mahalla an employee is being added to, and the new person's fields. */
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newRole, setNewRole] = useState<YettiligiRole>('raisi');

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/staff', {}));
      setItems(data.items as StaffRow[]);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 403) setItems([]);
      else setError(t('error.network'));
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
  }, [load]);



  /**
   * The photo goes up as RAW BYTES, not multipart.
   *
   * The route's content type IS the file's, which is what lets the server
   * decide from the first bytes whether it can decode it at all. A multipart
   * envelope around one photograph would add a parser and a boundary for no
   * gain — and `/mbt/media`, the multipart path, writes to a different bucket
   * with a different retention policy.
   */
  async function enrol(row: StaffRow, file: File | undefined): Promise<void> {
    if (!file) return;
    setBusy(row.member_id);
    setError(null);
    try {
      const mime = await sniffImageType(file);
      if (mime === null) {
        setError(t('staffdir.wrongFormat'));
        return;
      }
      const response = await fetch(
        `${apiBaseUrl()}/mbt/staff/${row.member_id}/face`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': mime },
          body: file,
        },
      );
      if (!response.ok) {
        /* The server's `detail` NAMES which of the five refusals it was — no
           face, two faces, too dark, not an image, model down. Showing a
           generic «upload failed» here would throw away the only part of the
           answer that tells somebody what to do differently. */
        const problem = (await response.json().catch(() => null)) as { detail?: string } | null;
        /* The STATUS when there is no detail. «Suratni roʻyxatga olib boʻlmadi»
           alone is unactionable and unreportable; a 413 or a 415 beside it
           tells whoever is looking which half of the system refused. */
        setError(problem?.detail ?? `${t('face.enrolFailed')} (HTTP ${response.status})`);
        return;
      }
      /*
        ONE FILE, BOTH EFFECTS — and this is the fix for a real trap.

        The card used to have TWO uploads: this one, and a hidden file input on
        the avatar that set only the citizen-card headshot with no face
        detection at all. Clicking the person's picture — the obvious thing to
        do — therefore stored a photo, ran no matching, and left the card
        reading «yuzi olinmagan» with nothing on screen to explain why. It was
        reported as «I upload a proper image and the face is not found», and it
        was exactly that.

        There is one control now, and «this is their photograph» does the whole
        of what somebody means by it: the face is enrolled AND the card gets the
        picture. If the citizen-card half fails the enrolment still stands —
        the face is what this screen is for.
      */
      if (mayEdit) await setDirectoryPhoto(row, file);
      await load();
    } catch {
      setError(t('error.network'));
    } finally {
      setBusy(null);
    }
  }

  async function removeFace(row: StaffRow): Promise<void> {
    setBusy(row.member_id);
    setError(null);
    try {
      await api().DELETE('/mbt/staff/{member_id}/face', {
        params: { path: { member_id: row.member_id } },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('face.deleteFailed'));
    } finally {
      setBusy(null);
    }
  }

  async function saveEdit(row: StaffRow): Promise<void> {
    if (!draftName.trim()) {
      setError(t('staffdir.nameRequired'));
      return;
    }
    setBusy(row.member_id);
    setError(null);
    try {
      await api().PATCH('/admin/yettiligi/{member_id}', {
        params: { path: { member_id: row.member_id } },
        body: {
          full_name: draftName.trim(),
          ...(draftPhone.trim() ? { phone: draftPhone.trim() } : {}),
        },
      });
      setEditing(null);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('staffdir.saveFailed'));
    } finally {
      setBusy(null);
    }
  }

  async function addMember(mahallaId: string): Promise<void> {
    if (!newName.trim() || !newPhone.trim()) {
      setError(t('staffdir.nameAndPhoneRequired'));
      return;
    }
    setBusy(mahallaId);
    setError(null);
    try {
      /*
        `hours` IS REQUIRED BY THE DIRECTORY and is sent as a full closed week.
        §2.3 wants every day present, and an empty array means closed — so a new
        employee starts with no published hours rather than with hours somebody
        invented. Whoever knows the real schedule sets it on the directory
        screen, and until then the citizen card shows nothing rather than a
        guess about when this person is at their desk.
      */
      const closed: components['schemas']['WeeklyHours'] = {
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
        sun: [],
      };
      await api().POST('/admin/yettiligi', {
        body: {
          mahalla_id: mahallaId,
          role: newRole,
          full_name: newName.trim(),
          phone: newPhone.trim(),
          hours: closed,
          is_active: true,
          /* The schema's own default. Sent explicitly because the generated
             type requires it — and 1000 puts a new person at the END of their
             mahalla's card rather than silently ahead of the raisi. */
          sort_order: 1000,
        },
      });
      setAddingTo(null);
      setNewName('');
      setNewPhone('');
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else if (caught instanceof ApiError && caught.status === 409) {
        // §2.3's partial unique index: one ACTIVE member per (mahalla, role).
        setError(t('staffdir.roleTaken'));
      } else setError(t('staffdir.addFailed'));
    } finally {
      setBusy(null);
    }
  }

  /**
   * The CITIZEN-CARD headshot, set from the same file as the enrolment.
   *
   * Called only after the face enrolment succeeds — see `enrol`. A failure here
   * is reported but does NOT undo the enrolment: the face is the thing this
   * screen exists for, and losing it because a second, cosmetic request failed
   * would be the wrong trade.
   */
  async function setDirectoryPhoto(row: StaffRow, file: File): Promise<void> {
    const form = new FormData();
    form.append('photo', file);
    const response = await fetch(
      `${apiBaseUrl()}/admin/yettiligi/${row.member_id}/photo`,
      { method: 'POST', credentials: 'include', body: form },
    );
    if (!response.ok) setError(t('staffdir.photoFailed'));
  }

  /*
    A SCREEN REFUSES OUT LOUD; a section could just not render.

    As a block at the foot of davomat this returned an empty fragment for
    somebody without the capability, which was right — a section that is not
    there is not a mystery. A ROUTE that renders nothing is a blank page, so it
    says which permission is missing instead.
  */
  if (!mayManage) {
    return <Notice tone="crit">{t('staffdir.noPermission')}</Notice>;
  }

  /**
   * The list, folded into its mahallas — IN THE ORDER THE SERVER SENT IT.
   *
   * `listMbtStaff` already orders by `mahallas.sort_order` then the member's
   * own sort order, which is §2.3's canonical ordering and the same order the
   * citizen card uses. Re-sorting alphabetically here would put this screen's
   * idea of the yettiligi out of step with the kiosk's, and the two would
   * disagree about who comes first in a building.
   */
  const groups = (() => {
    const out: { mahallaId: string; mahallaName: string; rows: StaffRow[] }[] = [];
    for (const row of items ?? []) {
      const last = out[out.length - 1];
      if (last && last.mahallaId === row.mahalla_id) last.rows.push(row);
      else out.push({ mahallaId: row.mahalla_id, mahallaName: row.mahalla_name, rows: [row] });
    }
    return out;
  })();

  const enrolled = (items ?? []).filter((row) => row.face_enrolled).length;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('staffdir.eyebrow')}</span>}
        title={t('nav.staff')}
        subtitle={t('staffdir.subtitle', { n: String(items?.length ?? 0) })}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {enrolled}
          </p>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('face.kpiEnrolled')}
          value={String(enrolled)}
          absent={t('app.notYet')}
          tone={enrolled > 0 ? 'ok' : 'neutral'}
          footerLabel={t('face.kpiOfStaff')}
          footerValue={String(items?.length ?? 0)}
        />
        <KpiCard
          label={t('face.kpiMahallas')}
          value={String(groups.length)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('face.kpiMahallasMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('face.kpiPending')}
          /* The work queue: everybody with no face yet. */
          value={String((items ?? []).filter((row) => !row.face_enrolled).length)}
          absent={t('app.notYet')}
          tone="warn"
          footerLabel={t('face.kpiPendingMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('face.kpiPin')}
          value={String((items ?? []).filter((row) => row.pin_set).length)}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={t('face.kpiPinMeaning')}
          footerValue=""
        />
      </div>

      <Notice tone="quiet">{t('staffdir.note')}</Notice>
      <Notice tone="quiet">{t('face.privacyNote')}</Notice>
      {error ? <Notice tone="warn">{error}</Notice> : null}

      <Panel>
        <PanelHeader title={t('staffdir.title')} aside={t('staffdir.hint')} />
        <div className="px-4 py-4">
          {items === null ? (
            <Skeleton className="h-48" />
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-label text-ink-500">{t('face.empty')}</p>
          ) : (
            <div className="space-y-6">
              {groups.map((group) => (
                <section key={group.mahallaId}>
                  {/* THE MAHALLA IS THE HEADING, and the count and the «add»
                      control belong to it — an employee is added TO a building,
                      and a form with a mahalla dropdown invites picking the
                      wrong one. */}
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline pb-1.5">
                    <h3 className="text-label font-semibold text-ink-100">
                      {group.mahallaName}
                      <span className="ml-2 text-micro font-normal tabular-nums text-ink-500">
                        {group.rows.length}
                      </span>
                    </h3>
                    {mayEdit ? (
                      <Button
                        tone="quiet"
                        onClick={() => {
                          setAddingTo(addingTo === group.mahallaId ? null : group.mahallaId);
                          setNewName('');
                          setNewPhone('');
                          setError(null);
                        }}
                      >
                        {t('staffdir.add')}
                      </Button>
                    ) : null}
                  </div>

                  {addingTo === group.mahallaId ? (
                    <div className="mb-3 space-y-3 rounded-card border border-brand bg-panel p-3 shadow-card">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <Field label={t('staffdir.name')}>
                          <TextInput
                            value={newName}
                            onChange={(event) => setNewName(event.target.value)}
                          />
                        </Field>
                        <Field label={t('staffdir.phone')} hint={t('staffdir.phoneHint')}>
                          <TextInput
                            value={newPhone}
                            onChange={(event) => setNewPhone(event.target.value)}
                          />
                        </Field>
                        <Field label={t('staffdir.role')} hint={t('staffdir.roleHint')}>
                          <select
                            value={newRole}
                            onChange={(event) => setNewRole(event.target.value as YettiligiRole)}
                            className="w-full rounded border border-hairline bg-panel px-3 py-2 text-label text-ink-100"
                          >
                            {YETTILIGI_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {roleLabel(role)}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => void addMember(group.mahallaId)}
                          disabled={busy === group.mahallaId}
                        >
                          {t('staffdir.addSave')}
                        </Button>
                        <Button tone="quiet" onClick={() => setAddingTo(null)}>
                          {t('face.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {group.rows.map((row) => (
                      <div
                        key={row.member_id}
                        className="flex flex-col gap-3 rounded-card border border-hairline bg-panel p-3 shadow-card"
                      >
                        <div className="flex items-start gap-3">
                          {/* THE ENROLMENT PHOTO WHEN THERE IS ONE, the
                              directory headshot otherwise. They are usually
                              different pictures, and «why does it not recognise
                              me» is answered by the one the matcher was built
                              from. */}
                          {row.face_photo_url || row.photo_url ? (
                              <img
                                src={`${apiBaseUrl().replace(/\/api\/v1$/, '')}${row.face_photo_url ?? row.photo_url}`}
                                alt=""
                                /*
                                  LAZY, because there are 225 of these. Without
                                  it the browser opens 225 requests the moment
                                  this section renders and every card shows a
                                  broken-image glyph until they land — which
                                  looks exactly like «these people have no
                                  photo» and was reported as that.
                                */
                                loading="lazy"
                                decoding="async"
                                /* And if one genuinely fails, it becomes the
                                   initial rather than a browser glyph. A broken
                                   icon tells a reader nothing they can act on. */
                                onError={(event) => {
                                  event.currentTarget.style.display = 'none';
                                }}
                                className="h-16 w-16 shrink-0 rounded-card object-cover"
                              />
                            ) : (
                              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-card bg-panel-raised text-lead font-semibold text-ink-500">
                                {row.full_name.trim().slice(0, 1).toUpperCase()}
                              </span>
                            )}

                          <div className="min-w-0 flex-1">
                            {editing === row.member_id ? (
                              <div className="space-y-2">
                                <TextInput
                                  value={draftName}
                                  onChange={(event) => setDraftName(event.target.value)}
                                />
                                <TextInput
                                  value={draftPhone}
                                  onChange={(event) => setDraftPhone(event.target.value)}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => void saveEdit(row)}
                                    disabled={busy === row.member_id}
                                  >
                                    {t('staffdir.save')}
                                  </Button>
                                  <Button tone="quiet" onClick={() => setEditing(null)}>
                                    {t('face.cancel')}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* `line-clamp-2`, not `truncate`: these are
                                    three-word Uzbek full names and one line is
                                    not enough for them. */}
                                <p className="line-clamp-2 text-label font-semibold leading-snug text-ink-100">
                                  {row.full_name}
                                </p>
                                <p className="truncate text-micro text-ink-500">
                                  {roleLabel(row.role)}
                                </p>
                                {row.phone ? (
                                  <p className="truncate text-nano tabular-nums text-ink-500">
                                    {row.phone}
                                  </p>
                                ) : null}
                                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-nano uppercase tracking-wide">
                                  {row.face_enrolled ? (
                                    <span className="text-ok">{t('face.enrolled')}</span>
                                  ) : (
                                    <span className="text-ink-500">{t('face.notEnrolled')}</span>
                                  )}
                                  {row.pin_set ? (
                                    <span className="text-ink-500">· {t('face.pinSet')}</span>
                                  ) : null}
                                </p>
                              </>
                            )}
                          </div>

                        </div>

                        {row.face_enrolled ? (
                          <p className="text-nano text-ink-500">
                            {t('face.enrolledOn', {
                              at: row.face_enrolled_at ? stamp(new Date(row.face_enrolled_at)) : '—',
                              quality: row.face_quality
                                ? String(Math.round(row.face_quality * 100))
                                : '—',
                            })}
                          </p>
                        ) : null}

                        {/*
                          NO CONSENT STEP. Removed on the owner's instruction
                          (2026-08-19); migration 0042 removed the database gate
                          in the same change and carries the reasoning.

                          The face controls are simply here now. The consent
                          COLUMNS and `postMbtStaffConsent` still exist, so an
                          organisation that decides to collect consent again has
                          the field and the evidence rule waiting.
                        */}
{/*
                          EVERY CONTROL IN ONE ROW AT THE FOOT, and the name
                          gets the card's full width above it.

                          «Oʻzgartirish» used to sit beside the name, which
                          cost the name about a third of the card — and these
                          are Uzbek full names, three words long.
                          «Gʻoyibnazarov Shu…» identifies nobody, which
                          defeats the only job the card has.
                        */}
                        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
                          {mayEdit && editing !== row.member_id ? (
                            <Button
                              tone="quiet"
                              onClick={() => {
                                setEditing(row.member_id);
                                setDraftName(row.full_name);
                                setDraftPhone(row.phone ?? '');
                              }}
                            >
                              {t('staffdir.edit')}
                            </Button>
                          ) : null}
                          <label className="cursor-pointer rounded border border-hairline px-3 py-1.5 text-label text-ink-100 transition-colors duration-state hover:bg-panel-raised">
                            {row.face_enrolled ? t('face.replace') : t('face.upload')}
                            <input
                              type="file"
                              accept={ACCEPT}
                              className="hidden"
                              disabled={busy === row.member_id}
                              onChange={(event) => void enrol(row, event.target.files?.[0])}
                            />
                          </label>
                          {row.face_enrolled ? (
                            <Button
                              tone="quiet"
                              onClick={() => void removeFace(row)}
                              disabled={busy === row.member_id}
                            >
                              {t('face.delete')}
                            </Button>
                          ) : null}
                        </div>
                        {busy === row.member_id ? (
                          <p className="text-nano text-ink-500">{t('face.working')}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
